/**
 * Search filter keys added by Plus round 3 (R3-27 experience fit, R3-34 shifts and hours). The
 * filters themselves are free and unmetered; this file is the one place that names them so the
 * several numeric-rule tables in the search code (SearchContext, the SSR page data, the map lens,
 * the salary strip and the Typesense builder) stay in step.
 *
 * Absence is never a value: a job whose ad does not state years or hours has no field, so an
 * "up to N years" filter matches ads that STATE at most N years. The copy says so.
 */

/** Numeric refinement keys and how they compare. `lte`: the ad's value must be at most the chosen one. */
export const PLUS_SEARCH_NUMERIC_RULE: Record<string, 'gte' | 'lte'> = {
  /** Ads that state at most this many years of required experience. */
  minimumYearsOfExperience: 'lte',
  /**
   * Weekly hours views. Both compare the ad's stated LOWER bound (`weeklyHoursMin`, which is also
   * the only number when the ad states one figure), so the two keys share one index field and are
   * mapped back to it by `plusRefinementKeyToAttribute`.
   */
  weeklyHoursAtMost: 'lte',
  weeklyHoursAtLeast: 'gte',
};

/** Refinement key -> the index field it filters. Keys not listed filter the field of the same name. */
const PLUS_REFINEMENT_KEY_TO_ATTRIBUTE: Record<string, string> = {
  weeklyHoursAtMost: 'weeklyHoursMin',
  weeklyHoursAtLeast: 'weeklyHoursMin',
};

export function plusRefinementKeyToAttribute(key: string): string {
  return PLUS_REFINEMENT_KEY_TO_ATTRIBUTE[key] ?? key;
}

/**
 * R3-27 owner default (2026-10-15): "up to N years" INCLUDES ads that state no minimum, because most
 * ads say nothing and dropping them hid the majority of real openings. A bool index field
 * `yearsNotStated` (true only when the ad gives no years) lets the filter pass them:
 * `(minimumYearsOfExperience:<=N || yearsNotStated:=true)`. The toggle `onlyStatedYears=true` switches
 * back to the strict "ads that state at most N years". Ads indexed before the field existed read as
 * "stated" until they are re-indexed, so the OR never hides a stated ad.
 */
export const ONLY_STATED_YEARS_KEY = 'onlyStatedYears';
export const YEARS_NOT_STATED_ATTRIBUTE = 'yearsNotStated';

/** Boolean refinement keys (true only; the index never stores false). */
export const SHIFT_FILTER_KEYS = ['isEveningWork', 'isWeekendWork', 'isShiftWork'] as const;
export type ShiftFilterKey = (typeof SHIFT_FILTER_KEYS)[number];

/** "Open to new graduates": ads that state at most this many years (0 and 1 count). */
export const GRADUATE_MAX_YEARS = 1;

/** Part-time view: ads whose stated (lower-bound) weekly hours are at most this many. */
export const PART_TIME_MAX_WEEKLY_HOURS = 30;
/** Full-time view: ads whose stated (lower-bound) weekly hours are at least this many. */
export const FULL_TIME_MIN_WEEKLY_HOURS = 35;
